/**
 * PLStatement — monthly Profit & Loss table (12 columns), gross/net aware, clickable cells.
 *
 * Extracted from FinancialsOverview so it can be reused on the Well Review tab.
 * The parent controls what a cell click does via onCellClick(row, month):
 *   - Financials page navigates to the month drill-down route.
 *   - Well Review shows the lease-scoped category breakdown inline.
 *
 * The "Less: LOE" row expands into its RollUp categories across every month. Category
 * amounts come from monthly[].loeByCategory / .wiLoeByCategory, which the financials API
 * builds on the exact same scope as loeOnly/wiLoeOnly — so sub-rows sum back to the parent.
 */
import { useState } from 'react'

function PLStatement({ monthly, isDark, viewMode, onCellClick, loeCategoryNames }) {
    const [loeExpanded, setLoeExpanded] = useState(false)
    // Filter to months with any activity
    const activeMonths = monthly.filter(m => m.grossRevenue > 0 || m.loeActual > 0)
    if (activeMonths.length === 0) return null

    const fmt = (val) => {
        if (val == null || val === 0) return '--'
        const abs = Math.abs(val)
        if (abs >= 1_000_000) return `${val < 0 ? '-' : ''}$${(abs / 1_000_000).toFixed(1)}M`
        if (abs >= 1_000) return `${val < 0 ? '-' : ''}$${(abs / 1_000).toFixed(0)}K`
        // sign goes before the $, not after it ("-$537", not "$-537")
        return `${val < 0 ? '-' : ''}$${Math.round(abs).toLocaleString()}`
    }

    const fmtNum = (val) => val ? Math.round(val).toLocaleString() : '--'
    const fmtPrice = (val) => val ? `$${val.toFixed(2)}` : '--'
    const fmtPct = (val) => val != null ? `${val.toFixed(1)}%` : '--'

    // Build P&L line items — each row defines a label, extractor, style, and optional clickType for drill-down
    const isNet = viewMode === 'net'
    const revKey = isNet ? 'nriRevenue' : 'grossRevenue'
    const bblKey = isNet ? 'netOilBBL' : 'oilBBL'
    const loeKey = isNet ? 'wiLoeOnly' : 'loeOnly'
    const loeActKey = isNet ? 'wiLoeActual' : 'loeActual'
    const woKey = isNet ? 'wiWorkover' : 'workover'
    const ohKey = isNet ? 'wiOverhead' : 'overhead'
    const avKey = isNet ? 'wiAdValorem' : 'adValorem'
    const capexKey = isNet ? 'wiCapex' : 'capex'
    // Production (allocated, 8/8) shown for context above Sales when available.
    // All downstream calcs (price, revenue, NOI) use SALES, not production.
    const hasProd = activeMonths.some(m => m.prodOilBBL != null)
    // Flag months where booked SALES (GL) diverge hard from PRODUCTION — a sign of
    // unsettled statements OR operator lease mis-coding (barrels booked to the wrong
    // lease), so the month's sales/revenue isn't mistaken for real performance.
    //   'under' = sales << production (this lease's barrels booked elsewhere / not settled)
    //   'over'  = sales >> production (another lease's barrels mis-coded onto this one)
    const bookingFlag = (m) => {
        if (!(m.prodOilBBL > 0) || m.oilBBL == null) return null
        if (m.oilBBL < 0.5 * m.prodOilBBL) return 'under'
        if (m.oilBBL > 2 * m.prodOilBBL) return 'over'
        return null
    }
    const flagTip = (m) => {
        const dir = bookingFlag(m)
        if (!dir) return undefined
        const base = `${fmtNum(m.oilBBL)} bbl sold vs ${fmtNum(m.prodOilBBL)} bbl produced.`
        return dir === 'under'
            ? `Under-booked: ${base} This lease's barrels are unsettled or mis-coded to another lease — sales/revenue understated.`
            : `Over-booked: ${base} Barrels from another lease are likely mis-coded onto this one — sales/revenue overstated.`
    }
    // LOE categories to show when the row is expanded. Prefer the API's ordering
    // (biggest spend first); fall back to deriving it from the visible months so the
    // row still expands if an older API response is cached.
    const loeCatKey = isNet ? 'wiLoeByCategory' : 'loeByCategory'
    const loeCats = (() => {
        if (loeCategoryNames?.length) {
            return loeCategoryNames.filter(c => activeMonths.some(m => (m[loeCatKey] || {})[c]))
        }
        const totals = {}
        activeMonths.forEach(m => {
            Object.entries(m[loeCatKey] || {}).forEach(([c, v]) => { totals[c] = (totals[c] || 0) + v })
        })
        return Object.entries(totals)
            .filter(([, v]) => v !== 0)
            .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
            .map(([c]) => c)
    })()

    const rows = [
        ...(hasProd ? [{ label: 'Production (BBL)', get: m => m.prodOilBBL, format: fmtNum, style: 'italic muted' }] : []),
        { label: isNet ? 'Net Oil Sales (BBL)' : 'Oil Sales (BBL)', get: m => m[bblKey], format: fmtNum, style: 'italic', flagUnderbooked: true },
        { label: 'Avg Price ($/BBL)', get: m => m[bblKey] > 0 ? m.oilRevenue / m[bblKey] : null, format: fmtPrice, style: 'italic' },
        { label: isNet ? 'NRI Revenue' : 'Gross Revenue', get: m => m[revKey], format: fmt, style: 'bold green' },
        ...(!isNet ? [
            { label: 'Less: Royalties', get: m => m.royalties, format: fmt, style: 'indent', clickType: 'royalties' },
            { label: 'Net Revenue', get: m => m.grossRevenue - m.royalties, format: fmt, style: 'bold' },
        ] : []),
        { label: 'Less: Production Taxes', get: m => -m.severanceTax, format: fmt, style: 'indent', clickType: 'production-tax' },
        {
            label: 'Less: LOE',
            get: m => -m[loeKey],
            format: fmt,
            style: 'indent',
            clickType: 'loe-only',
            expandable: loeCats.length > 0,
            expanded: loeExpanded,
            onToggle: () => setLoeExpanded(v => !v)
        },
        ...(loeExpanded ? loeCats.map(cat => ({
            label: cat,
            get: m => {
                const amt = (m[loeCatKey] || {})[cat]
                return amt ? -amt : null
            },
            format: fmt,
            style: 'subitem',
            clickType: 'loe-only',
            category: cat
        })) : []),
        { label: 'Less: Workover', get: m => -m[woKey], format: fmt, style: 'indent', clickType: 'workover' },
        { label: 'Less: Overhead', get: m => -m[ohKey], format: fmt, style: 'indent', clickType: 'overhead' },
        { label: 'Less: Ad Valorem', get: m => -m[avKey], format: fmt, style: 'indent', clickType: 'advalorem' },
        {
            label: 'Total Expenses',
            get: m => -(m[loeActKey] + m[ohKey] + m[avKey] + m.severanceTax),
            format: fmt,
            style: 'bold'
        },
        {
            label: 'Net Operating Income',
            get: m => {
                const topLine = isNet ? m.nriRevenue : (m.grossRevenue - m.royalties)
                return topLine - m.severanceTax - m[loeActKey] - m[ohKey] - m[avKey]
            },
            format: fmt,
            style: 'bold green-red'
        },
        {
            label: 'NOI Margin',
            get: m => {
                const topLine = isNet ? m.nriRevenue : (m.grossRevenue - m.royalties)
                const noi = topLine - m.severanceTax - m[loeActKey] - m[ohKey] - m[avKey]
                const denom = m[revKey]
                return denom > 0 ? (noi / denom) * 100 : null
            },
            format: fmtPct,
            style: 'bold italic'
        },
        { type: 'separator' },
        { label: 'CapEx', get: m => m[capexKey], format: fmt, style: 'indent', clickType: 'capex' },
    ]

    const headerClass = isDark ? 'text-gray-400 bg-slate-800/80' : 'text-gray-500 bg-gray-100'
    const cellBase = `px-3 py-2 text-right whitespace-nowrap text-sm`

    const getRowClasses = (style) => {
        const classes = []
        if (style?.includes('bold')) classes.push('font-bold')
        if (style?.includes('italic')) classes.push('italic')
        if (style?.includes('indent')) classes.push('pl-6')
        if (style?.includes('subitem')) classes.push(isDark ? 'text-gray-400' : 'text-gray-500')
        if (style?.includes('muted')) classes.push(isDark ? 'text-gray-500' : 'text-gray-400')
        return classes.join(' ')
    }

    const getCellColor = (style, val) => {
        if (style?.includes('subitem')) return isDark ? 'text-gray-400' : 'text-gray-500'
        if (style?.includes('muted')) return isDark ? 'text-gray-500' : 'text-gray-400'
        if (style?.includes('green-red')) {
            return val > 0
                ? (isDark ? 'text-green-400' : 'text-green-600')
                : val < 0 ? (isDark ? 'text-red-400' : 'text-red-600') : ''
        }
        if (style?.includes('green')) return isDark ? 'text-green-400' : 'text-green-600'
        return ''
    }

    return (
        <div className="overflow-x-auto">
            <table className={`w-full text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <thead>
                    <tr className={headerClass}>
                        <th className={`text-left px-3 py-2 font-semibold sticky left-0 z-10 min-w-[160px] ${isDark ? 'bg-slate-800/80' : 'bg-gray-100'}`}>
                            Line Item
                        </th>
                        {activeMonths.map(m => (
                            <th key={m.ym} className="px-3 py-2 text-right font-semibold whitespace-nowrap min-w-[90px]">
                                {bookingFlag(m) && (
                                    <span className="text-amber-500 mr-1 cursor-help" title={flagTip(m)}>⚠</span>
                                )}
                                {m.shortLabel}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => {
                        if (row.type === 'separator') {
                            return (
                                <tr key={i}>
                                    <td colSpan={activeMonths.length + 1}
                                        className={`py-1 ${isDark ? 'border-t-2 border-slate-600' : 'border-t-2 border-gray-300'}`} />
                                </tr>
                            )
                        }

                        const isSub = row.style?.includes('subitem')
                        const rowClasses = getRowClasses(row.style)
                        const borderClass = row.style?.includes('bold')
                            ? (isDark ? 'border-t border-slate-600' : 'border-t border-gray-300')
                            : isSub
                                ? ''
                                : (isDark ? 'border-t border-slate-700/50' : 'border-t border-gray-200/70')

                        return (
                            <tr key={row.category ? `cat:${row.category}` : `row:${row.label}`}
                                className={`${borderClass} ${isSub ? (isDark ? 'bg-slate-800/40' : 'bg-gray-50/70') : ''} ${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'}`}>
                                <td className={`px-3 whitespace-nowrap sticky left-0 z-10 ${isSub ? 'py-1' : 'py-2'} ${isDark ? 'bg-slate-900' : 'bg-white'} ${rowClasses}`}>
                                    {row.expandable ? (
                                        <button type="button"
                                            onClick={row.onToggle}
                                            title={row.expanded ? 'Collapse LOE categories' : 'Expand into LOE categories'}
                                            className={`inline-flex items-center gap-1 -ml-1 px-1 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`}>
                                            <span className={`inline-block text-[10px] leading-none transition-transform ${row.expanded ? 'rotate-90' : ''}`}>▶</span>
                                            {row.label}
                                        </button>
                                    ) : isSub ? (
                                        <span className="pl-6 text-xs">{row.label}</span>
                                    ) : row.label}
                                </td>
                                {activeMonths.map(m => {
                                    const val = row.get(m)
                                    const flagged = row.flagUnderbooked && bookingFlag(m)
                                    const color = flagged
                                        ? (isDark ? 'text-amber-400' : 'text-amber-600')
                                        : getCellColor(row.style, val)
                                    const clickable = row.clickType && val != null && val !== 0 && typeof onCellClick === 'function'
                                    return (
                                        <td key={m.ym}
                                            className={`${cellBase} ${isSub ? 'py-1 text-xs' : ''} ${rowClasses} ${color} ${clickable ? 'cursor-pointer hover:underline' : ''}`}
                                            title={flagged ? flagTip(m) : undefined}
                                            onClick={clickable ? () => onCellClick(row, m) : undefined}>
                                            {row.format(val)}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default PLStatement
